import { mapActions, mapGetters } from 'vuex'
import {
  TECHNICAL_FILTER_QUERY_KEYS,
  technicalFilterApiParams,
  technicalRangesContext,
} from '@/utils/technicalFilters'

const sortingValueForQuery = query =>
  query.sort ? query.sort : query.q ? null : '-popular'

export default {
  data() {
    return {
      total_products: 0,
      sortingValue: sortingValueForQuery(this.$route.query),
      exactResults: true,
      loading: false,
      limit: 36,
      page: this.$route.query?.page ?? 1,
      perPage: 5,
      technicalRanges: {},
      technicalRangesKey: null,
    }
  },

  computed: {
    ...mapGetters({
      products_list: 'products_list',
    }),
    products: {
      get() {
        return this.products_list ? this.products_list : null
      },
      set(value) {
        this.setProducts(value)
      },
    },
    sortingValueFromRoute() {
      return sortingValueForQuery(this.$route.query)
    },
    selectedVehicleId() {
      return this.$store.state.selected_vehicle?.id
    },
  },

  watch: {
    $route() {
      this.page = this.$route.query.page ? this.$route.query.page : 1
      this.sortingValue = this.sortingValueFromRoute
      this.setProducts([])
      this.$fetch()
    },
    selectedVehicleId() {
      if (!this.$route.query.q) return

      this.setProducts([])
      this.$fetch()
    },
    sortingValue(newVal) {
      if (newVal === this.sortingValueFromRoute) return

      this.$router.push(
        this.localePath({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            sort: newVal || undefined,
          },
        })
      )
    },
    page(newVal) {
      if (String(newVal) === String(this.$route.query.page || 1)) return

      this.$router.push(
        this.localePath({
          query: {
            ...this.$route.query,
            page: newVal,
          },
        })
      )
    },
  },

  methods: {
    deleteAllFilters() {
      this.$router.push(
        this.localePath({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            page: undefined,
            sort: undefined,
            filters: undefined,
            categories: undefined,
            type: undefined,
            technologie: undefined,
            brands: undefined,
            reviews: undefined,
            price: undefined,
            ...Object.fromEntries(
              TECHNICAL_FILTER_QUERY_KEYS.map(key => [key, undefined])
            ),
          },
        })
      )
    },

    async loadFetch($state) {
      this.$store.dispatch('setLoadingProducts', true)

      const rangesKey = technicalRangesContext(
        this.$route.path,
        this.$route.query
      )
      const withRanges = rangesKey !== this.technicalRangesKey

      const sharedParams = {
        page: this.page,
        limit: this.limit,
        'filter[reviewStars]': this.$route.query.reviews || undefined,
        'filter[filterValuesIds]': this.$route.query.filters || undefined,
        'filter[brandsIds]': this.$route.query.q
          ? undefined
          : this.$route.query.brands || undefined,
        'filter[headlightTypesIds]': this.$route.query.q
          ? undefined
          : this.$route.query.type || undefined,
        'filter[headlightVehicleDetailsIds]': this.$route.query.q
          ? undefined
          : this.$route.query.technologie || undefined,
        'filter[priceRange]': this.$route.query.price || undefined,
        ...technicalFilterApiParams(this.$route.query),
        with_technical_ranges: withRanges ? 1 : undefined,
        sort: this.sortingValue || undefined,
      }

      const vehicleId = this.$route.params.typeid
      const searchQuery = this.$route.query.q
      const fetchPromise = searchQuery
        ? this.$productService.fetchSiteSearchProducts({
            ...sharedParams,
            q: searchQuery,
            vehicle: this.selectedVehicleId,
          })
        : this.category && this.category.id
          ? this.$productService.fetchCategoryPageProducts(this.category.id, sharedParams)
          : vehicleId
            ? this.$vehicleService.fetchVehicleSearchPageProducts(vehicleId, sharedParams)
            : null

      if (!fetchPromise) {
        this.$store.dispatch('setLoadingProducts', false)
        return
      }

      await fetchPromise
        .then(res => {
          const requestedPage = parseInt(this.page)
          const lastPage = res.meta?.last_page

          if (
            !$state &&
            requestedPage > 1 &&
            lastPage &&
            requestedPage > lastPage
          ) {
            this.$store.dispatch('setLoadingProducts', false)

            if (process.server) {
              this.$404Error()
              return
            }

            this.$router.replace(
              this.localePath({
                path: this.$route.path,
                query: { ...this.$route.query, page: undefined },
              })
            )
            return
          }

          this.setProducts(res.data)
          this.$store.dispatch(
            'setSearchVehiclePacks',
            res.meta?.other_vehicle_packs || 0
          )

          this.total_products = res.meta.total
          if (withRanges) {
            this.technicalRanges = res.meta.technical_ranges || {}
            this.technicalRangesKey = rangesKey
          }
          this.setPagination({
            page: parseInt(this.page),
            limit: this.limit,
            total: this.total_products,
          })

          if (this.structuredData) {
            this.structuredData.products_recursive_count = res.meta.total
          }

          this.exactResults = res.exactResults

          if ($state && res.data.length > 0) {
            $state.loaded()
          } else if ($state) {
            $state.complete()
          }

          this.$store.dispatch('setLoadingProducts', false)
        })
        .catch(() => {
          this.setProducts(null)
          this.$swalErrorCustomMessage(
            this.$t('mixins.productListMixin.productsListNotLoaded')
          )
          this.$store.dispatch('setLoadingProducts', false)
        })
    },

    ...mapActions({
      setProducts: 'setProducts',
      pushProducts: 'pushProducts',
      setPagination: 'setPagination',
    }),
  },

  async fetch() {
    await this.loadFetch()
  },
}
